import { useLocation, useNavigate } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { ToastSuccess, ToastError } from "../components/toast-notification";
import { me, logoutDevice, myApps, createApp, updateApp, deleteApp } from '../api/apiService';
import CommitActivity from '../components/CommitActivity';

function Profile() {
  const navigate = useNavigate();
  const [profileObj, setProfileObj] = useState({});
  const [activeTab, setActiveTab] = useState('profile');
  const [apps, setApps] = useState([]);
  const [newAppForm, setNewAppForm] = useState({ appName: '', webhookUrl: '', redirectUrl: '' });
  const [isCreatingApp, setIsCreatingApp] = useState(false);
  const [isAppModalOpen, setIsAppModalOpen] = useState(false);
  const [editingAppId, setEditingAppId] = useState(null);
  const [showSsoDialog, setShowSsoDialog] = useState(false);
  const [ssoAppName, setSsoAppName] = useState('');
  const location = useLocation();

  useEffect(() => {
    if (location.state && location.state.ssoAuthSuccess && location.state.appName) {
      setShowSsoDialog(true);
      setSsoAppName(location.state.appName);
    }
  }, [location.state]);

  const fetchProfileData = () => {
    me()
      .then(r => setProfileObj(r?.data?.data))
      .catch(e => { ToastError(e?.response?.data?.message); navigate("/login", { replace: true }); });
    myApps()
      .then(r => setApps(r?.data?.data || []))
      .catch(e => console.error("Failed to fetch apps", e));
  };

  useEffect(() => {
    const localToken = localStorage.getItem("x-device-access-token");
    const queryParameters = new URLSearchParams(location.search);
    const queryToken = queryParameters.get("accessToken");
    if (queryToken) {
      localStorage.setItem("x-device-access-token", queryToken);
      let userId = 'user';
      try { const payload = JSON.parse(atob(queryToken.split('.')[1])); userId = payload.userId || 'user'; } catch (e) {}
      navigate(`/${userId}/profile`, { replace: true });
    } else if (localToken) {
      let userId = 'user';
      try { const payload = JSON.parse(atob(localToken.split('.')[1])); userId = payload.userId || 'user'; } catch (e) {}
      if (location.pathname !== `/${userId}/profile`) {
        navigate(`/${userId}/profile`, { replace: true, state: location.state });
        return;
      }
      fetchProfileData();
    } else {
      navigate("/login", { replace: true });
    }
  }, [location.pathname, location.search]);

  const logout = () => {
    logoutDevice()
      .then(r => { localStorage.clear(); sessionStorage.clear(); ToastSuccess(r?.data.message); navigate("/login", { replace: true }); })
      .catch(e => ToastError(e?.response?.data?.message));
  };

  const handleSaveApp = (e) => {
    e.preventDefault();
    setIsCreatingApp(true);
    if (editingAppId) {
      updateApp(editingAppId, newAppForm)
        .then(r => { ToastSuccess(r?.data?.message); setApps(apps.map(a => a.appId === editingAppId ? r.data.data : a)); setIsAppModalOpen(false); setIsCreatingApp(false); })
        .catch(e => { ToastError(e?.response?.data?.message); setIsCreatingApp(false); });
    } else {
      createApp(newAppForm)
        .then(r => { ToastSuccess(r?.data?.message); setApps([...apps, r.data.data]); setIsAppModalOpen(false); setIsCreatingApp(false); })
        .catch(e => { ToastError(e?.response?.data?.message); setIsCreatingApp(false); });
    }
  };

  const openCreateModal = () => { setEditingAppId(null); setNewAppForm({ appName: '', webhookUrl: '', redirectUrl: '' }); setIsAppModalOpen(true); };
  const openEditModal = (app) => { setEditingAppId(app.appId); setNewAppForm({ appName: app.appName, webhookUrl: app.webhookUrl || '', redirectUrl: app.redirectUrl || '' }); setIsAppModalOpen(true); };
  const handleDeleteApp = (appId) => {
    if (window.confirm("CRITICAL WARNING: INITIATING DELETE SEQUENCE. CONFIRM?")) {
      deleteApp(appId).then(() => { ToastSuccess("App deleted."); setApps(apps.filter(a => a.appId !== appId)); }).catch(e => ToastError(e?.response?.data?.message));
    }
  };

  const defaultAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(profileObj.fullName || "User")}&background=050505&color=0ff&size=150&bold=true`;

  const navItems = [
    { tab: 'profile', label: 'SYS.PROFILE', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
    { tab: 'security', label: 'SEC.OVERRIDE', icon: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z' },
  ];

  if (profileObj?.userScopes?.includes('ROOT')) {
    navItems.push({ tab: 'developer', label: 'DEV.TERMINAL', icon: 'M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4' });
  }

  return (
    <div className="min-h-screen text-white">
      {/* Cyber Header */}
      <header className="cyber-header sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center border border-cyan-400 bg-cyan-900/30" style={{ clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%)' }}>
              <svg className="w-4 h-4 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z"/>
              </svg>
            </div>
            <span className="font-bold text-sm tracking-[0.2em] uppercase text-cyan-400 drop-shadow-[0_0_5px_rgba(0,255,255,0.5)]">EXTENDS_WARE</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1 bg-black/50 border border-green-500/50">
            <span className="w-2 h-2 bg-green-500 shadow-[0_0_8px_#0f0] animate-pulse"/>
            <span className="text-[10px] font-bold text-green-500 uppercase tracking-widest">Sys_Online</span>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex flex-col lg:flex-row gap-8">

          {/* Sidebar */}
          <aside className="w-full lg:w-64 flex-shrink-0 space-y-6">
            {/* Avatar Card */}
            <div className="cyber-card">
              <div className="h-24 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] border-b border-cyan-400/20 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-b from-cyan-900/40 to-transparent" />
              </div>
              <div className="px-6 pb-6 -mt-12 flex flex-col items-center text-center relative z-10">
                <div className="relative mb-4 group">
                  <img src={profileObj.profileImage || defaultAvatar} alt="Profile"
                    className="w-24 h-24 object-cover border-2 border-cyan-400 shadow-[0_0_15px_rgba(0,255,255,0.3)] transition-all duration-300 group-hover:border-fuchsia-500 group-hover:shadow-[0_0_20px_rgba(255,0,255,0.4)]"
                    style={{ clipPath: 'polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px)' }}/>
                  <span className="absolute -bottom-2 -right-2 w-6 h-6 border border-black bg-green-500 shadow-[0_0_10px_#0f0] z-20" style={{ clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%)' }}/>
                </div>
                <h3 className="font-bold text-white text-base uppercase tracking-wider text-shadow-sm">{profileObj.fullName || 'Loading...'}</h3>
                <p className="cyber-label text-[10px] mt-1 w-full truncate">{profileObj.email}</p>
                {profileObj.userScopes?.[0] && (
                  <span className="mt-3 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] bg-fuchsia-900/30 text-fuchsia-400 border border-fuchsia-500 shadow-[0_0_10px_rgba(255,0,255,0.2)]">
                    {profileObj.userScopes[0]}
                  </span>
                )}
              </div>
            </div>

            {/* Nav */}
            <nav className="cyber-card p-4 space-y-2">
              {navItems.map(({ tab, label, icon }) => (
                <button key={tab} onClick={() => setActiveTab(tab)}
                  className={`w-full flex items-center gap-4 px-4 py-3 text-sm font-bold uppercase tracking-widest ${
                    activeTab === tab ? 'cyber-nav-active' : 'cyber-nav-inactive'
                  }`}>
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={2} d={icon}/>
                  </svg>
                  {label}
                </button>
              ))}
              <div className="my-4 border-t border-cyan-400/20"/>
              <button onClick={logout}
                className="w-full flex items-center gap-4 px-4 py-3 text-sm font-bold uppercase tracking-widest text-red-500 hover:bg-red-900/20 hover:border-l-4 hover:border-red-500 hover:text-red-400 transition-all border-l-4 border-transparent shadow-[inset_0_0_0_rgba(255,0,0,0)] hover:shadow-[inset_20px_0_20px_-20px_rgba(255,0,0,0.5)]">
                <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
                </svg>
                SYS.LOGOUT
              </button>
            </nav>
          </aside>

          {/* Main Panel */}
          <main className="flex-1 min-w-0 space-y-6">
            {activeTab === 'profile' && (
              <>
                <div className="flex items-center justify-between border-b border-cyan-400/20 pb-2">
                  <div>
                    <h1 className="text-xl font-black uppercase tracking-widest text-cyan-400 drop-shadow-[0_0_8px_rgba(0,255,255,0.4)]">> PROFILE_DATA</h1>
                    <p className="cyber-label text-[10px] mt-1">Accessing personal databanks...</p>
                  </div>
                </div>

                <div className="cyber-card p-6 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {[
                      { label: 'OP_NAME', value: profileObj.fullName || '—' },
                      {
                        label: 'COM_LINK',
                        value: (
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium">{profileObj.email || '—'}</span>
                            {profileObj.isEmailVerified && (
                              <span className="px-2 py-0.5 border border-green-500 bg-green-900/30 text-green-400 text-[9px] font-bold uppercase tracking-widest shadow-[0_0_5px_rgba(0,255,0,0.3)]">
                                Verified
                              </span>
                            )}
                          </div>
                        )
                      },
                      {
                        label: 'ACCESS_LEVEL',
                        value: (
                          <span className="px-2 py-1 border border-fuchsia-500 bg-fuchsia-900/30 text-fuchsia-400 text-[10px] font-bold uppercase tracking-[0.2em] shadow-[0_0_5px_rgba(255,0,255,0.3)]">
                            {profileObj.userScopes?.[0] || 'User'}
                          </span>
                        )
                      },
                      {
                        label: 'AUTH_VECTOR',
                        value: (
                          <div className="flex items-center gap-2">
                            {profileObj.signupUsing === 'google' && <span className="text-cyan-400 font-bold uppercase text-[10px] border border-cyan-400 p-1">GGL</span>}
                            {profileObj.signupUsing === 'github' && <span className="text-cyan-400 font-bold uppercase text-[10px] border border-cyan-400 p-1">GHB</span>}
                            <span className="text-sm font-medium uppercase">
                              {(!profileObj.signupUsing || profileObj.signupUsing === 'email') ? 'STANDARD_ENCRYPTION' : profileObj.signupUsing}
                            </span>
                          </div>
                        )
                      },
                    ].map(({ label, value }, i) => (
                      <div key={i} className="flex flex-col gap-1">
                        <span className="cyber-label text-[11px]">{label}</span>
                        <div className="text-white text-lg tracking-wide">{value}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <CommitActivity />
              </>
            )}

            {activeTab === 'security' && (
              <>
                <div className="flex items-center justify-between border-b border-cyan-400/20 pb-2">
                  <div>
                    <h1 className="text-xl font-black uppercase tracking-widest text-fuchsia-400 drop-shadow-[0_0_8px_rgba(255,0,255,0.4)]">> SEC_OVERRIDE</h1>
                    <p className="cyber-label text-[10px] mt-1 text-fuchsia-300">Modify encryption keys...</p>
                  </div>
                </div>

                <div className="cyber-card p-8 max-w-md space-y-6 border-fuchsia-500/30">
                  {[
                    { id: 'cp', label: 'CURRENT_KEY' },
                    { id: 'np', label: 'NEW_KEY' },
                    { id: 'cnp', label: 'VERIFY_KEY' },
                  ].map(f => (
                    <div key={f.id}>
                      <label className="cyber-label block mb-2">{f.label}</label>
                      <input type="password" placeholder="********" className="cyber-input w-full p-3 text-sm font-mono"/>
                    </div>
                  ))}
                  <button className="cyber-btn cyber-btn-magenta w-full py-4 mt-4">
                    INITIATE OVERRIDE
                  </button>
                </div>
              </>
            )}

            {activeTab === 'developer' && (
              <>
                <div className="flex items-center justify-between border-b border-cyan-400/20 pb-2">
                  <div>
                    <h1 className="text-xl font-black uppercase tracking-widest text-cyan-400 drop-shadow-[0_0_8px_rgba(0,255,255,0.4)]">> DEV_TERMINAL</h1>
                    <p className="cyber-label text-[10px] mt-1">Manage connected nodes...</p>
                  </div>
                  <button onClick={openCreateModal} className="cyber-btn px-4 py-2 flex items-center gap-2 text-xs">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={2.5} d="M12 4v16m8-8H4"/>
                    </svg>
                    INITIALIZE_NODE
                  </button>
                </div>

                {apps.length === 0 ? (
                  <div className="cyber-card p-16 flex flex-col items-center justify-center text-center border-dashed border-cyan-400/30">
                    <div className="w-16 h-16 border border-cyan-400 bg-cyan-900/20 flex items-center justify-center mb-6" style={{ clipPath: 'polygon(0 10px, 10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%)' }}>
                      <svg className="w-8 h-8 text-cyan-400 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
                      </svg>
                    </div>
                    <p className="text-sm font-bold uppercase tracking-widest text-cyan-300">NO ACTIVE NODES DETECTED</p>
                    <p className="cyber-label text-[10px] mt-2">Initialize a new node to begin.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {apps.map(app => (
                      <div key={app.appId} className="cyber-card p-6 flex flex-col group hover:border-cyan-400">
                        <div className="flex justify-between items-start mb-6">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 border border-cyan-400 bg-cyan-900/40 flex items-center justify-center shadow-[0_0_10px_rgba(0,255,255,0.2)] group-hover:shadow-[0_0_15px_rgba(0,255,255,0.5)] transition-all" style={{ clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%)' }}>
                              <span className="text-xl font-black text-cyan-300">{app.appName.charAt(0).toUpperCase()}</span>
                            </div>
                            <div>
                              <h4 className="font-bold text-white text-lg tracking-wide uppercase">{app.appName}</h4>
                              <div className="flex items-center gap-2 mt-1">
                                <span className={`w-2 h-2 ${app.isActive ? 'bg-green-500 shadow-[0_0_8px_#0f0]' : 'bg-red-500 shadow-[0_0_8px_#f00]'}`}/>
                                <span className="cyber-label text-[9px]">{app.isActive ? 'STATUS: ONLINE' : 'STATUS: OFFLINE'}</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-4 mb-6 flex-1">
                          <div className="bg-black/60 border border-cyan-900 p-3">
                            <p className="cyber-label text-[9px] mb-1">CLIENT_ID</p>
                            <div className="flex justify-between items-center cursor-pointer group/copy" onClick={() => { navigator.clipboard.writeText(app.appId); }}>
                              <code className="text-xs font-mono text-cyan-300 drop-shadow-[0_0_2px_rgba(0,255,255,0.8)] truncate">{app.appId}</code>
                              <svg className="w-4 h-4 text-cyan-700 group-hover/copy:text-cyan-300 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>
                            </div>
                          </div>
                          {app.webhookUrl && (
                            <div className="bg-black/60 border border-cyan-900 p-3">
                              <p className="cyber-label text-[9px] mb-1">WEBHOOK_URL</p>
                              <p className="text-xs text-white/70 font-mono truncate">{app.webhookUrl}</p>
                            </div>
                          )}
                          {app.redirectUrl && (
                            <div className="bg-black/60 border border-cyan-900 p-3">
                              <p className="cyber-label text-[9px] mb-1">REDIRECT_URL</p>
                              <p className="text-xs text-white/70 font-mono truncate">{app.redirectUrl}</p>
                            </div>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <button onClick={() => openEditModal(app)} className="cyber-btn py-2 text-xs">RECONFIGURE</button>
                          <button onClick={() => handleDeleteApp(app.appId)} className="cyber-btn py-2 text-xs border-red-500 text-red-500 hover:bg-red-900/30 hover:shadow-[0_0_15px_rgba(255,0,0,0.4)]">TERMINATE</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>

      {/* Cyber Modal */}
      {isAppModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="w-full max-w-lg cyber-card border-cyan-400">
            <div className="flex items-center justify-between p-6 border-b border-cyan-400/30 bg-cyan-900/20">
              <h3 className="text-lg font-black uppercase tracking-widest text-cyan-400">{editingAppId ? '> RECONFIGURE_NODE' : '> INITIALIZE_NODE'}</h3>
              <button onClick={() => setIsAppModalOpen(false)} className="text-cyan-600 hover:text-cyan-300 transition-colors">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
            <form onSubmit={handleSaveApp} className="p-6 space-y-6">
              {[
                { key: 'appName', label: 'NODE_DESIGNATION', placeholder: 'e.g. ALPHA_NET', type: 'text', required: true },
                { key: 'redirectUrl', label: 'REDIRECT_VECTOR', placeholder: 'http://localhost:3000/callback', type: 'url', required: false },
                { key: 'webhookUrl', label: 'WEBHOOK_UPLINK', placeholder: 'http://localhost:4000/webhook', type: 'url', required: false },
              ].map(f => (
                <div key={f.key}>
                  <label className="cyber-label block mb-2">{f.label} {f.required && <span className="text-red-500">*</span>}</label>
                  <input
                    required={f.required}
                    type={f.type}
                    placeholder={f.placeholder}
                    value={newAppForm[f.key]}
                    onChange={e => setNewAppForm({ ...newAppForm, [f.key]: e.target.value })}
                    className="cyber-input w-full p-3 text-sm font-mono"/>
                </div>
              ))}
              <div className="flex gap-4 pt-4">
                <button type="button" onClick={() => setIsAppModalOpen(false)} className="cyber-btn flex-1 py-3 border-gray-500 text-gray-400 hover:bg-gray-800 hover:shadow-none">ABORT</button>
                <button type="submit" disabled={isCreatingApp} className="cyber-btn flex-1 py-3">
                  {isCreatingApp ? 'PROCESSING...' : 'EXECUTE'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Cyber SSO Dialog */}
      {showSsoDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md p-4">
          <div className="w-full max-w-sm cyber-card border-green-500 p-8 text-center shadow-[0_0_50px_rgba(0,255,0,0.15)]">
            <div className="w-20 h-20 mx-auto border-2 border-green-500 bg-green-900/20 flex items-center justify-center mb-6 shadow-[0_0_20px_rgba(0,255,0,0.5)]" style={{ clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' }}>
              <svg className="w-10 h-10 text-green-400 drop-shadow-[0_0_5px_#0f0]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={3} d="M5 13l4 4L19 7"/>
              </svg>
            </div>
            <h3 className="text-xl font-black uppercase tracking-widest text-green-400 drop-shadow-[0_0_8px_rgba(0,255,0,0.5)] mb-4">UPLINK ESTABLISHED</h3>
            <p className="cyber-label text-xs mb-8">
              SECURE CONNECTION TO <strong className="text-white text-sm">{ssoAppName}</strong> VERIFIED.
            </p>
            <div className="flex flex-col gap-4">
              <button onClick={() => { window.open('', '_self', ''); window.close(); }} className="cyber-btn py-3 border-green-500 text-green-400 bg-green-900/20 hover:bg-green-900/40 hover:shadow-[0_0_20px_rgba(0,255,0,0.5)]">
                CLOSE_TERMINAL
              </button>
              <button onClick={() => { window.history.replaceState({}, document.title); setShowSsoDialog(false); }} className="cyber-label text-xs hover:text-white transition-colors underline decoration-dotted underline-offset-4">
                REMAIN_IN_SYSTEM
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Profile;
